import { useCallback, useContext } from "react";
import { sendMessageToChat } from "../api/chat";
import { addChat } from "../api/chatMessage";
import useAnalytics from "../hooks/useAnalytics";
import { useChatBotConfigContext } from "../providers/chatBotConfigContext";
import { matchQuestion } from "../utils/checkFAQQuestionExists";
import identifyMarkdownLinks from "../utils/identifyMarkdownLinks";
import { GlobalStateContext } from "../../../context/GlobalStateContext";

const generateError = (
  messageId,
  message = "We apologize, but an error has occurred. Please try again."
) => ({
  id: messageId,
  text: message,
  time: new Date().toISOString(),
  isBotMessage: true,
  isLoading: false,
  isReactable: false,
});

export const useChatStream = ({
  setNewMessageText,
  setMessage,
  appendMessage,
  setIsChatLoading,
  replaceId,
  messages,
}) => {
  const { captureEvent } = useAnalytics();
  const { chatBotData, chatBotConfig } = useChatBotConfigContext();
  const { userData } = useAnalytics();
  const { filters } = useContext(GlobalStateContext);

  const handleSendMessage = useCallback(
    async (newMessageText, chatBotFAQsQuestions, onAddedNewMessages) => {
      if (!newMessageText || !chatBotData?.companyId) return;

      // Insert a new message to backend and database
      if (userData?.distinct_id) {
        await addChat({
          chatBotId: chatBotConfig?.id || "",
          text: newMessageText,
          userId: userData?.distinct_id,
          isBotMessage: false,
          companyId: chatBotData.companyId,
        });
      }

      const messageId = Date.now().toString();

      setMessage({
        id: `${messageId}-user`,
        text: newMessageText,
        isBotMessage: false,
        isLoading: false,
        time: new Date().toISOString(),
        isReactable: false,
      });

      setNewMessageText("");
      setIsChatLoading(true);

      onAddedNewMessages?.(true);

      try {
        if (chatBotFAQsQuestions && chatBotFAQsQuestions.length > 0) {
          const questionObj = matchQuestion(
            newMessageText,
            chatBotFAQsQuestions
          );

          if (questionObj) {
            setIsChatLoading(false);

            captureEvent("FaqQuestion", { query: questionObj.question });

            let id = null;

            if (userData?.distinct_id) {
              // save the chatbot response to database
              const botChat = await addChat({
                chatBotId: chatBotConfig?.id || "",
                text: questionObj.answer,
                userId: userData?.distinct_id,
                isBotMessage: true,
                companyId: chatBotData.companyId,
              });

              id = botChat.id;
            }

            setMessage({
              id: id || messageId,
              text: questionObj.answer,
              time: new Date().toISOString(),
              isLoading: false,
              isBotMessage: true,
              isReactable: !!id,
            });

            onAddedNewMessages?.(true);

            return;
          }
        }

        appendMessage({
          id: messageId,
          text: "",
          isLoading: true,
          isBotMessage: true,
        });

        onAddedNewMessages?.(true);

        if (!chatBotData.companyId) throw new Error();

        const recentMessages = messages?.slice(-6);
        const formattedHistory = recentMessages?.reduce((acc, message) => {
          if (!message.isBotMessage) {
            const lastEntry = acc[acc.length - 1];

            if (!lastEntry || lastEntry.response !== "") {
              acc.push({ question: message.text, response: "" });
            } else {
              lastEntry.question += message.text;
            }
          } else {
            const lastEntry = acc[acc.length - 1];
            if (lastEntry) {
              lastEntry.response += message.text;
            }
          }
          return acc;
        }, []);

        const stream = await sendMessageToChat(
          newMessageText,
          formattedHistory,
          filters
        );

        setIsChatLoading(false);

        const reader = stream?.getReader();

        if (!reader) return;

        let responseText = "";

        const readStream = async () => {
          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { done, value } = await reader.read();

            if (done) break;

            appendMessage({
              id: messageId,
              text: value,
              isLoading: false,
              isBotMessage: true,
            });

            onAddedNewMessages?.(true);

            responseText = responseText + value;
          }

          replaceId?.(messageId, {
            id: messageId,
            text: responseText,
            time: new Date().toISOString(),
            isLoading: false,
            isBotMessage: true,
            isReactable: true,
          });
        };

        readStream()
          .catch((error) => {
            appendMessage(generateError(messageId, error.message));
            onAddedNewMessages?.(true);
            console.error("Error reading stream:", error);
          })
          .finally(async () => {
            setIsChatLoading(false);

            if (responseText.length) {
              // save the chatbot response to database
              if (chatBotData.companyId && userData?.distinct_id) {
                const chatData = await addChat({
                  chatBotId: chatBotConfig?.id || "",
                  text: responseText,
                  userId: userData.distinct_id,
                  isBotMessage: true,
                  companyId: chatBotData.companyId,
                });

                replaceId?.(messageId, {
                  id: chatData.id,
                  text: responseText,
                  time: new Date().toISOString(),
                  isLoading: false,
                  isBotMessage: true,
                  isReactable: true,
                });
              }

              // handing analytics events
              const links = identifyMarkdownLinks(responseText);

              links.forEach((link) => {
                captureEvent("ProvidedSourceLink", { source_link: link });
              });
            }
          });
      } catch (e) {
        setIsChatLoading(false);
        appendMessage(generateError(messageId));
        onAddedNewMessages?.(true);
      }
    },
    [chatBotData, messages]
  );

  return {
    handleSendMessage,
  };
};

export const useChatFAQStream = ({
  setNewMessageText,
  setMessage,
  appendMessage,
  setIsChatLoading,
}) => {
  const { captureEvent } = useAnalytics();
  const { chatBotData, chatBotConfig } = useChatBotConfigContext();
  const { userData } = useAnalytics();

  const handleSendFAQMessage = useCallback(
    async (faq, onAddedNewMessages) => {
      if (!faq || !chatBotData?.companyId) return;

      let id = null;

      if (userData?.distinct_id) {
        // Insert a new message to backend and database
        const userChat = await addChat({
          chatBotId: chatBotConfig?.id || "",
          text: faq.question,
          userId: userData?.distinct_id,
          isBotMessage: false,
          companyId: chatBotData.companyId,
        });

        id = userChat.id;
      }

      const messageId = Date.now().toString();

      setMessage({
        id: id || `${messageId}-faq`,
        text: faq.question,
        isBotMessage: false,
        isLoading: false,
        time: new Date().toISOString(),
      });

      setNewMessageText("");

      setIsChatLoading(true);

      onAddedNewMessages?.(true);

      try {
        setIsChatLoading(false);

        let newId = null;

        if (userData?.distinct_id) {
          // save the chatbot response to database
          const botChat = await addChat({
            chatBotId: chatBotConfig?.id || "",
            text: faq.answer,
            userId: userData?.distinct_id,
            isBotMessage: true,
            companyId: chatBotData.companyId,
          });

          newId = botChat.id;
        }

        setMessage({
          id: newId || messageId,
          text: faq.answer,
          time: new Date().toISOString(),
          isLoading: false,
          isBotMessage: true,
          isReactable: !!newId,
        });

        // handing analytics events
        const links = identifyMarkdownLinks(faq.answer);
        links.forEach((link) => {
          captureEvent("ProvidedSourceLink", { source_link: link });
        });
      } catch (e) {
        appendMessage(generateError(messageId));
      } finally {
        setIsChatLoading(false);
        onAddedNewMessages?.(true);
      }
    },
    [chatBotData]
  );

  return {
    handleSendFAQMessage,
  };
};

export const useSmoothScroll = (chatContainerRef) => {
  return useCallback(
    (isSmooth = true) => {
      const chatContainer = chatContainerRef.current;

      if (chatContainer) {
        chatContainer.scroll({
          top: chatContainer.scrollHeight,
          behavior: isSmooth ? "smooth" : "auto",
        });
      }
    },
    [chatContainerRef]
  );
};
